class CartDrawer extends HTMLElement {
  open(trigger = document.activeElement) {
    this.openedBy = trigger;
    this.hidden = false;
    document.body.classList.add('cart-drawer-open');
    window.requestAnimationFrame(() => this.querySelector('[data-cart-drawer-close]')?.focus());
  }

  close() {
    this.hidden = true;
    document.body.classList.remove('cart-drawer-open');
    this.openedBy?.focus?.();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

const refreshCartDrawer = async (openDrawer = false) => {
  const root = window.themeRoutes?.root || '/';
  const response = await fetch(`${root}?section_id=cart-drawer`);
  if (!response.ok) throw new Error('Unable to refresh cart.');

  const html = await response.text();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const replacement = parsed.querySelector('cart-drawer');
  const current = document.querySelector('cart-drawer');
  if (!replacement || !current) return;

  current.replaceWith(replacement);
  if (openDrawer) replacement.open();
};

document.addEventListener('theme:cart:updated', async (event) => {
  try {
    await refreshCartDrawer(event.detail?.openDrawer === true);
  } catch (error) {
    const status = document.getElementById('ThemeStatus');
    if (status) status.textContent = error.message;
  }
});

document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-cart-drawer-trigger]');
  if (trigger) {
    event.preventDefault();
    document.querySelector('cart-drawer')?.open(trigger);
    return;
  }

  if (event.target.closest('[data-cart-drawer-close]')) {
    document.querySelector('cart-drawer')?.close();
    return;
  }

  const quantityButton = event.target.closest('[data-cart-quantity-action]');
  if (quantityButton) {
    const input = quantityButton.closest('[data-cart-line]')?.querySelector('[data-cart-quantity-input]');
    if (!input) return;

    const step = Number(input.step) || 1;
    const direction = quantityButton.dataset.cartQuantityAction === 'increase' ? 1 : -1;
    const minimum = Number(input.dataset.minimum) || 1;
    const maximum = input.max ? Number(input.max) : Infinity;
    const currentQuantity = Number(input.value);
    const nextQuantity = direction > 0
      ? Math.min(maximum, currentQuantity + step)
      : (currentQuantity <= minimum ? 0 : Math.max(minimum, currentQuantity - step));
    await updateCartLine(input, nextQuantity, quantityButton);
    return;
  }

  const removeButton = event.target.closest('[data-cart-remove]');
  if (removeButton) await updateCartLine(removeButton, 0, removeButton);
});

document.addEventListener('change', async (event) => {
  const input = event.target.closest('[data-cart-quantity-input]');
  if (!input) return;
  await updateCartLine(input, normalizeCartQuantity(input, Number(input.value)), input);
});

document.addEventListener('keydown', (event) => {
  const drawer = document.querySelector('cart-drawer:not([hidden])');
  if (!drawer) return;

  if (event.key === 'Escape') {
    drawer.close();
    return;
  }

  if (event.key !== 'Tab') return;
  const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')];
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

async function updateCartLine(source, quantity, loadingTarget) {
  const lineContainer = source.closest('[data-cart-line]');
  const line = Number(source.dataset.line || lineContainer?.dataset.cartLine);
  const status = document.getElementById('ThemeStatus');
  if (!line || !window.themeCart) return;

  loadingTarget.disabled = true;
  loadingTarget.setAttribute('aria-busy', 'true');
  try {
    await window.themeCart.change(line, quantity);
  } catch (error) {
    if (source.matches?.('[data-cart-quantity-input]')) source.value = source.defaultValue;
    const message = source.closest('cart-drawer')?.dataset.updateError || error.message;
    if (status) status.textContent = message;
  } finally {
    loadingTarget.disabled = false;
    loadingTarget.removeAttribute('aria-busy');
  }
}

function normalizeCartQuantity(input, quantity) {
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  const minimum = Number(input.dataset.minimum) || 1;
  const increment = Number(input.step) || 1;
  const maximum = input.max ? Number(input.max) : Infinity;
  const stepped = minimum + (Math.round((quantity - minimum) / increment) * increment);
  return Math.min(maximum, Math.max(minimum, stepped));
}
